import React from 'react';
import {
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import HierarchyItem from './HierarchyItem';
import { OccupationHierarchy } from './types';

interface TreeNode extends OccupationHierarchy {
  children: TreeNode[];
  expanded?: boolean;
}

interface HierarchyNodeProps {
  node: TreeNode;
  level: number;
  expandedNodes: Set<number>;
  onToggleExpand: (nodeId: number) => void;
  onUpdateHierarchy: (occupationId: number, parentId: number | null, display_priority: number) => Promise<void>;
  onUpdateOccupation: (occupationId: number, name: string, display_priority?: number) => Promise<void>;
  onDeleteOccupation: (occupationId: number) => Promise<void>;
  onTogglePublished: (occupationId: number) => Promise<void>;
  allOccupations: OccupationHierarchy[];
}

const HierarchyNode: React.FC<HierarchyNodeProps> = ({
  node,
  level,
  expandedNodes,
  onToggleExpand,
  onUpdateHierarchy,
  onUpdateOccupation,
  onDeleteOccupation,
  onTogglePublished,
  allOccupations,
}) => {
  const isExpanded = expandedNodes.has(node.id);
  const hasChildren = node.children.length > 0;

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    transform,
    isDragging,
  } = useDraggable({
    id: node.id,
  });

  const {
    setNodeRef: setDropRef,
    isOver,
  } = useDroppable({
    id: `drop-${node.id}`,
    data: {
      type: 'node',
      node,
      level,
    },
  });

  const dragStyle = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      className="hierarchy-node"
      style={{ marginLeft: `${(level - 1) * 30}px` }}
    >
      <div className="d-flex align-items-center mb-2">
        {hasChildren && (
          <button
            type="button"
            className="btn btn-sm btn-link p-0 me-2"
            onClick={() => onToggleExpand(node.id)}
            style={{ minWidth: '20px' }}
          >
            <i
              className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}
            ></i>
          </button>
        )}
        {!hasChildren && <span className="me-2" style={{ width: '20px' }}></span>}
        <div
          ref={setDropRef}
          className={`flex-grow-1 p-2 border rounded d-flex align-items-center justify-content-between ${
            isOver ? 'border-primary bg-light' : 'bg-white'
          }`}
        >
          <div
            ref={setDragRef}
            style={dragStyle}
            className={`flex-grow-1 ${isDragging ? 'opacity-50' : ''}`}
          >
            <HierarchyItem
              occupation={node}
              level={level}
              onUpdateOccupation={onUpdateOccupation}
              onTogglePublished={onTogglePublished}
              {...attributes}
              {...listeners}
            />
          </div>
          <button
            type="button"
            className="btn btn-sm btn-danger ms-2"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`「${node.name}」を削除しますか？\n\n注意: 子職種は削除されず、親なしの状態になります。`)) {
                onDeleteOccupation(node.id);
              }
            }}
            title="削除"
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div className="children-container">
          {node.children.map((child) => (
            <HierarchyNode
              key={child.id}
              node={child}
              level={level + 1}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
              onUpdateHierarchy={onUpdateHierarchy}
              onUpdateOccupation={onUpdateOccupation}
              onDeleteOccupation={onDeleteOccupation}
              onTogglePublished={onTogglePublished}
              allOccupations={allOccupations}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HierarchyNode;

